import type {SDKSettings} from '@/app/(app)/sdk/[gid]/types';
import type {ResolvedTheme} from '@/services/partner/types';

/**
 * Repaint the shell game (19369) SDK theme with the partner's brand colour so the
 * reused @sdk-mobile side menu / deposit buttons read as the partner's, not the SDK
 * default cobalt blue (#4890E8).
 *
 * The SDK menu paints from `settings.theme.gameTheme` (getDynamicTheme → SkillzStyle),
 * NOT from the `--partner-*` CSS vars the server-rendered surfaces use — RN-web reads
 * JS style objects. So we overlay the handful of "brand blue" keys with the partner
 * colours. v6 splits brand ACCENT from NAV/positive state (Skillz = magenta accent
 * but GREEN nav/tier/tab) — so buttons + links take the accent, while tab highlight,
 * tier progress and medals take the nav colour (`tabActive`, falling back to accent
 * for single-brand partners like Lycos). Semantic currency colours (cash green,
 * ticketz orange, Z blue) are left untouched — they mean the same everywhere.
 *
 * No-ops (returns the input) when there's no partner accent, so an unthemed partner
 * keeps the default SDK look. Server-side + pure (no window) so the layout can call it.
 */
export default function partnerizeShellTheme(
  settings: SDKSettings,
  theme: ResolvedTheme,
  opts: {surfaces?: boolean} = {}
): SDKSettings {
  const accent = theme.primary || theme.accent;
  const gameTheme = settings.theme?.gameTheme;
  if (!accent || !gameTheme) return settings;

  // Nav / positive state — green on Skillz, = accent on single-brand partners.
  const nav = theme.tabActive || accent;
  const sheetStyles = gameTheme.sheetStyles || {};
  const gradients = gameTheme.gradients || {};

  const accentGradient = [accent, accent];

  // Brand keys — always overlaid (shell menu/deposit + game buttons/nav/tier/links).
  const brandSheet = {
    ...sheetStyles,
    progressBarColor: {...sheetStyles.progressBarColor, backgroundColor: nav},
    tabHighlightColor: {...sheetStyles.tabHighlightColor, color: nav},
    textColorMedals: {...sheetStyles.textColorMedals, color: nav},
    textColorLink: {...sheetStyles.textColorLink, color: accent},
  };

  // Full game-lobby SURFACE override (§10.2), applied ONLY when the partner checked
  // "Theme game screens" (useOwnTheme). Maps the v6 lobby tokens onto the SkillzStyle
  // surface keys the game lobby renders from: page bg, card/cell surfaces, dividers,
  // and body/muted text. RN reads concrete colours (no color-mix), so we fall back to
  // bg/text when a surface token wasn't set. Currency colours are untouched.
  const surfaceSheet =
    opts.surfaces && theme.background && theme.text
      ? (() => {
          const bg = theme.background as string;
          const card = theme.card || bg;
          const border = theme.cardBorder || card;
          const text = theme.text as string;
          const muted = theme.textMuted || text;
          const bgc = (c: string) => ({backgroundColor: c});
          const col = (c: string) => ({color: c});
          return {
            viewPrimaryColor: {...sheetStyles.viewPrimaryColor, ...bgc(bg)},
            viewSecondaryColor: {...sheetStyles.viewSecondaryColor, ...bgc(card)},
            viewTertiaryColor: {...sheetStyles.viewTertiaryColor, ...bgc(card)},
            cellBackgroundColor: {...sheetStyles.cellBackgroundColor, ...bgc(card)},
            cellAlternateBackgroundColor: {...sheetStyles.cellAlternateBackgroundColor, ...bgc(card)},
            viewDividerPrimaryColor: {...sheetStyles.viewDividerPrimaryColor, ...bgc(border)},
            viewDividerColorTwo: {...sheetStyles.viewDividerColorTwo, ...bgc(border)},
            textColorPrimary: {...sheetStyles.textColorPrimary, ...col(text)},
            textColorSecondary: {...sheetStyles.textColorSecondary, ...col(muted)},
            textColorTertiary: {...sheetStyles.textColorTertiary, ...col(muted)},
          };
        })()
      : {};

  return {
    ...settings,
    theme: {
      ...settings.theme,
      gameTheme: {
        ...gameTheme,
        sheetStyles: {...brandSheet, ...surfaceSheet},
        gradients: {
          ...gradients,
          btnGradientOne: accentGradient,
          btnGradientTwo: accentGradient,
          btnGradientThree: accentGradient,
          sideMenuHighlightedGradient: accentGradient,
        },
      },
    },
  };
}
